import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FileText, Upload, Plus, Search, MoreVertical, Trash2,
  Calendar, Clock, CheckCircle, AlertCircle, ChevronRight, X, File
} from 'lucide-react';

const DOC_TYPES = [
  { value: 'policy', label: 'Policy' },
  { value: 'contract', label: 'Contract' },
  { value: 'handbook', label: 'Handbook' },
  { value: 'guideline', label: 'Guideline' },
];

const DOC_TYPE_COLORS = {
  policy: { bg: 'bg-blue-500/10', text: 'text-blue-500' },
  contract: { bg: 'bg-purple-500/10', text: 'text-purple-500' },
  handbook: { bg: 'bg-emerald-500/10', text: 'text-emerald-500' },
  guideline: { bg: 'bg-amber-500/10', text: 'text-amber-500' },
};

const Policies = () => {
  const { user, organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    name: '', document_type: 'policy', description: '', version: '1.0',
    effective_date: '', expiry_date: '',
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMode, setUploadMode] = useState('file'); // 'file' or 'paste'
  const [pasteText, setPasteText] = useState('');

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#09090b] border-[#27272a] text-white placeholder-[#52525b]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999]'} focus:outline-none focus:ring-1 focus:ring-[#586f8d]`;

  useEffect(() => {
    if (!organizationId) return;
    loadDocuments();
  }, [organizationId]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('policy_documents')
        .select('*, document_sections(count)')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setDocuments(data || []);
    } catch (e) {
      console.error('Error loading documents:', e);
    }
    setLoading(false);
  };

  const handleUpload = async () => {
    if (!uploadForm.name.trim()) {
      toast.error('Please enter a document name');
      return;
    }
    if (uploadMode === 'paste' && !pasteText.trim()) {
      toast.error('Please paste your document text');
      return;
    }

    setUploading(true);
    try {
      let storagePath = null;
      let fileName = null;
      let fileType = null;
      let fileSizeBytes = null;
      let fullText = null;

      if (uploadMode === 'paste' && pasteText.trim()) {
        fullText = pasteText.trim();
      } else if (uploadFile) {
        fileName = uploadFile.name;
        fileType = uploadFile.type;
        fileSizeBytes = uploadFile.size;

        // Upload to storage
        storagePath = `${organizationId}/${Date.now()}_${uploadFile.name}`;

        const { error: uploadError } = await supabase.storage
          .from('policy-documents')
          .upload(storagePath, uploadFile);

        if (uploadError) {
          console.warn('Storage upload failed:', uploadError.message);
          storagePath = null;
        }

        // If it's a text file, read contents directly
        if (uploadFile.type === 'text/plain' || uploadFile.name.endsWith('.txt')) {
          fullText = await uploadFile.text();
        }
      }

      const { data, error } = await supabase
        .from('policy_documents')
        .insert({
          organization_id: organizationId,
          name: uploadForm.name.trim(),
          document_type: uploadForm.document_type,
          description: uploadForm.description.trim() || null,
          version: uploadForm.version || '1.0',
          storage_path: storagePath,
          file_name: fileName,
          file_type: fileType,
          file_size_bytes: fileSizeBytes,
          full_text: fullText,
          effective_date: uploadForm.effective_date || null,
          expiry_date: uploadForm.expiry_date || null,
          uploaded_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      // If we have text content, auto-parse it into sections
      if (fullText && data) {
        await parseSections(data.id, fullText);
      }

      toast.success('Document uploaded successfully');
      setShowUpload(false);
      setUploadForm({ name: '', document_type: 'policy', description: '', version: '1.0', effective_date: '', expiry_date: '' });
      setUploadFile(null);
      setPasteText('');
      setUploadMode('file');
      loadDocuments();
    } catch (e) {
      console.error('Upload error:', e);
      toast.error('Failed to upload document');
    }
    setUploading(false);
  };

  const parseSections = async (documentId, text) => {
    // Simple client-side parsing — split by numbered sections or paragraphs
    const lines = text.split('\n').filter(l => l.trim());
    const sections = [];
    let currentSection = null;
    let sortOrder = 0;

    for (const line of lines) {
      const trimmed = line.trim();

      // Match numbered sections like "1.", "1.1", "1.1.1", "Section 1:", "Clause 1:"
      const sectionMatch = trimmed.match(/^(?:(?:Section|Clause|Part|Article)\s+)?(\d+(?:\.\d+)*)[.:)\s]\s*(.*)$/i);
      const headingMatch = !sectionMatch && trimmed.match(/^(#{1,4})\s+(.+)$|^([A-Z][A-Z\s]{3,})$/);

      if (sectionMatch) {
        if (currentSection) sections.push(currentSection);
        const sectionNum = sectionMatch[1];
        const rest = sectionMatch[2] || '';
        const depth = sectionNum.split('.').length - 1;
        currentSection = {
          document_id: documentId,
          organization_id: organizationId,
          section_number: sectionNum,
          section_type: depth === 0 ? 'section' : depth === 1 ? 'subsection' : 'paragraph',
          title: rest.length > 0 && rest.length < 120 ? rest : null,
          content: rest.length >= 120 ? rest : '',
          depth,
          sort_order: sortOrder++,
        };
      } else if (headingMatch) {
        if (currentSection) sections.push(currentSection);
        const title = headingMatch[2] || headingMatch[3];
        currentSection = {
          document_id: documentId,
          organization_id: organizationId,
          section_number: null,
          section_type: 'section',
          title: title.trim(),
          content: '',
          depth: 0,
          sort_order: sortOrder++,
        };
      } else if (currentSection) {
        currentSection.content += (currentSection.content ? '\n' : '') + trimmed;
      } else {
        // Standalone paragraph
        currentSection = {
          document_id: documentId,
          organization_id: organizationId,
          section_number: null,
          section_type: 'paragraph',
          title: null,
          content: trimmed,
          depth: 0,
          sort_order: sortOrder++,
        };
      }
    }

    if (currentSection) sections.push(currentSection);

    if (sections.length > 0) {
      // Ensure all sections have non-empty content
      const validSections = sections.filter(s => s.content || s.title).map(s => ({
        ...s,
        content: s.content || s.title || '',
      }));

      if (validSections.length > 0) {
        await supabase.from('document_sections').insert(validSections);
        await supabase.from('policy_documents').update({
          is_parsed: true,
          parsed_at: new Date().toISOString(),
        }).eq('id', documentId);
      }
    }
  };

  const handleDelete = async (docId) => {
    try {
      // Delete sections first
      await supabase.from('document_sections').delete().eq('document_id', docId);
      // Delete document
      const { error } = await supabase.from('policy_documents').delete().eq('id', docId);
      if (error) throw error;
      toast.success('Document deleted');
      setDeleteConfirm(null);
      loadDocuments();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const filtered = documents.filter(d => {
    if (filterType !== 'all' && d.document_type !== filterType) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return (d.name?.toLowerCase().includes(q) || d.description?.toLowerCase().includes(q));
    }
    return true;
  });

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
            Policies & Contracts
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
            Upload and manage your organisation's policies, contracts, and guidelines
          </p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> Upload Document
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className={`${inputClass} pl-9`}
          />
        </div>
        <div className={`flex items-center gap-1 p-1 rounded-lg border ${isDark ? 'border-[#27272a] bg-[#121212]' : 'border-[#e5e5e5] bg-white'}`}>
          {[{ value: 'all', label: 'All' }, ...DOC_TYPES].map(t => (
            <button
              key={t.value}
              onClick={() => setFilterType(t.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filterType === t.value
                  ? isDark ? 'bg-[#27272a] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
                  : isDark ? 'text-[#52525b] hover:text-white' : 'text-[#999] hover:text-[#1a1a1a]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Upload Sheet */}
      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20" onClick={() => setShowUpload(false)}>
          <div className="fixed inset-0 bg-black/50" />
          <div
            className={`relative z-10 w-full max-w-lg rounded-xl border shadow-2xl p-6 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className={`text-lg font-semibold font-[Manrope] ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Upload Document</h2>
              <button onClick={() => setShowUpload(false)} className={isDark ? 'text-[#52525b] hover:text-white' : 'text-[#999] hover:text-[#1a1a1a]'}>
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Upload mode toggle */}
              <div className={`flex items-center gap-1 p-1 rounded-lg border ${isDark ? 'border-[#27272a] bg-[#09090b]' : 'border-[#e5e5e5] bg-[#f9f9f9]'}`}>
                <button
                  onClick={() => setUploadMode('file')}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    uploadMode === 'file'
                      ? isDark ? 'bg-[#27272a] text-white' : 'bg-white text-[#1a1a1a] shadow-sm'
                      : isDark ? 'text-[#52525b] hover:text-white' : 'text-[#999] hover:text-[#1a1a1a]'
                  }`}
                >
                  Upload File
                </button>
                <button
                  onClick={() => setUploadMode('paste')}
                  className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    uploadMode === 'paste'
                      ? isDark ? 'bg-[#27272a] text-white' : 'bg-white text-[#1a1a1a] shadow-sm'
                      : isDark ? 'text-[#52525b] hover:text-white' : 'text-[#999] hover:text-[#1a1a1a]'
                  }`}
                >
                  Paste Text
                </button>
              </div>

              {/* File Drop or Paste */}
              {uploadMode === 'file' ? (
                <div>
                  <label className={`flex flex-col items-center justify-center h-28 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                    uploadFile
                      ? isDark ? 'border-[#586f8d]/30 bg-[#586f8d]/5' : 'border-[#586f8d]/30 bg-[#586f8d]/5'
                      : isDark ? 'border-[#27272a] hover:border-[#3f3f46]' : 'border-[#e5e5e5] hover:border-[#ccc]'
                  }`}>
                    {uploadFile ? (
                      <div className="text-center">
                        <File className={`h-6 w-6 mx-auto mb-1 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} />
                        <span className={`text-sm ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{uploadFile.name}</span>
                        <span className={`text-xs block ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{formatFileSize(uploadFile.size)}</span>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Upload className={`h-6 w-6 mx-auto mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
                        <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>PDF, DOCX, or TXT (max 50MB)</span>
                      </div>
                    )}
                    <input type="file" className="hidden" accept=".pdf,.docx,.doc,.txt" onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) {
                        setUploadFile(f);
                        if (!uploadForm.name) setUploadForm(prev => ({ ...prev, name: f.name.replace(/\.[^/.]+$/, '') }));
                      }
                    }} />
                  </label>
                </div>
              ) : (
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Document Text</label>
                  <textarea
                    className={`${inputClass} resize-none h-36 font-mono text-xs`}
                    value={pasteText}
                    onChange={e => setPasteText(e.target.value)}
                    placeholder="Paste your policy or contract text here...&#10;&#10;Use numbered sections (1., 1.1, 2., etc.) for automatic parsing."
                  />
                </div>
              )}

              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Document Name *</label>
                <input className={inputClass} value={uploadForm.name} onChange={e => setUploadForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. Safeguarding Policy 2024" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Type</label>
                  <select className={inputClass} value={uploadForm.document_type} onChange={e => setUploadForm(prev => ({ ...prev, document_type: e.target.value }))}>
                    {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Version</label>
                  <input className={inputClass} value={uploadForm.version} onChange={e => setUploadForm(prev => ({ ...prev, version: e.target.value }))} placeholder="1.0" />
                </div>
              </div>

              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Description</label>
                <textarea className={`${inputClass} resize-none h-16`} value={uploadForm.description} onChange={e => setUploadForm(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description of this document..." />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Effective Date</label>
                  <input type="date" className={inputClass} value={uploadForm.effective_date} onChange={e => setUploadForm(prev => ({ ...prev, effective_date: e.target.value }))} />
                </div>
                <div>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Expiry Date</label>
                  <input type="date" className={inputClass} value={uploadForm.expiry_date} onChange={e => setUploadForm(prev => ({ ...prev, expiry_date: e.target.value }))} />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowUpload(false)}
                className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'text-[#a1a1aa] hover:bg-[#18181b]' : 'text-[#666] hover:bg-[#f5f5f5]'}`}
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" /> Upload
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className={`${cardClass} h-40 animate-pulse`} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className={`text-center py-16 ${cardClass}`}>
          <FileText className={`h-10 w-10 mx-auto mb-3 ${isDark ? 'text-[#27272a]' : 'text-[#e5e5e5]'}`} />
          <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
            {documents.length === 0 ? 'No documents yet' : 'No matching documents'}
          </h3>
          <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
            {documents.length === 0 ? 'Upload your policies, contracts, and guidelines to get started' : 'Try adjusting your search or filters'}
          </p>
          {documents.length === 0 && (
            <button
              onClick={() => setShowUpload(true)}
              className="mt-4 flex items-center gap-2 px-4 py-2 mx-auto rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90"
            >
              <Plus className="h-4 w-4" /> Upload First Document
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(doc => {
            const typeColor = DOC_TYPE_COLORS[doc.document_type] || DOC_TYPE_COLORS.policy;
            const sectionCount = Array.isArray(doc.document_sections) ? doc.document_sections[0]?.count ?? doc.document_sections.length : 0;
            const isExpired = doc.expiry_date && new Date(doc.expiry_date) < new Date();

            return (
              <div
                key={doc.id}
                className={`${cardClass} p-4 cursor-pointer transition-all group ${isDark ? 'hover:border-[#3f3f46]' : 'hover:border-[#ccc]'} relative`}
                onClick={() => navigate(`/policies/${doc.id}`)}
              >
                {/* Delete button */}
                <button
                  onClick={e => { e.stopPropagation(); setDeleteConfirm(doc.id); }}
                  className={`absolute top-3 right-3 p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'hover:bg-[#27272a] text-[#52525b] hover:text-red-400' : 'hover:bg-[#f5f5f5] text-[#999] hover:text-red-500'}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>

                {/* Delete confirmation */}
                {deleteConfirm === doc.id && (
                  <div className={`absolute inset-0 z-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-[#121212]/95' : 'bg-white/95'}`} onClick={e => e.stopPropagation()}>
                    <div className="text-center">
                      <p className={`text-sm mb-3 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Delete this document?</p>
                      <div className="flex gap-2 justify-center">
                        <button onClick={() => setDeleteConfirm(null)} className={`px-3 py-1.5 text-xs rounded-md ${isDark ? 'text-[#a1a1aa] hover:bg-[#18181b]' : 'text-[#666] hover:bg-[#f5f5f5]'}`}>Cancel</button>
                        <button onClick={() => handleDelete(doc.id)} className="px-3 py-1.5 text-xs rounded-md text-white bg-red-500 hover:bg-red-600">Delete</button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Header */}
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${typeColor.bg}`}>
                    <FileText className={`h-4 w-4 ${typeColor.text}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{doc.name}</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${typeColor.bg} ${typeColor.text}`}>
                        {doc.document_type}
                      </span>
                      {doc.version && (
                        <span className={`text-[10px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>v{doc.version}</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                {doc.description && (
                  <p className={`text-xs mb-3 line-clamp-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{doc.description}</p>
                )}

                {/* Footer info */}
                <div className={`flex items-center justify-between pt-3 border-t ${isDark ? 'border-[#1e1e1e]' : 'border-[#f0f0f0]'}`}>
                  <div className="flex items-center gap-3">
                    {doc.is_parsed ? (
                      <span className="flex items-center gap-1 text-[10px] text-emerald-500">
                        <CheckCircle className="h-3 w-3" /> {sectionCount} sections
                      </span>
                    ) : (
                      <span className={`flex items-center gap-1 text-[10px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                        <Clock className="h-3 w-3" /> Not parsed
                      </span>
                    )}
                    {isExpired && (
                      <span className="flex items-center gap-1 text-[10px] text-red-400">
                        <AlertCircle className="h-3 w-3" /> Expired
                      </span>
                    )}
                  </div>
                  <span className={`text-[10px] ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                    {doc.created_at ? new Date(doc.created_at).toLocaleDateString([], { day: 'numeric', month: 'short' }) : ''}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Policies;
